import { useEffect, useRef, useState } from "react";
import { useAppContext } from "../context/AppContext";
import { ItemModel } from "../models/ItemModel";
import itemService from "../services/itemService";

interface EditItemShippingPolicyCompProps {
    show: boolean;
    item: ItemModel;
    onClose: () => void;
    onItemShippingPolicyUpdated: (updatedItem: ItemModel) => void;
}

interface ItemShippingPolicyFormErrors {
    itemShippingPolicy?: string;
}

const EditItemShippingPolicyComp: React.FC<EditItemShippingPolicyCompProps> = ({
    show,
    item,
    onClose,
    onItemShippingPolicyUpdated
}) => {
    const modalRef = useRef<HTMLDivElement | null>(null);
    const bsModalRef = useRef<any>(null);
    const { seller, setSeller } = useAppContext();

    const [storeShippingPolicy, setStoreShippingPolicy] = useState(seller!.shippingPolicy);
    const [itemShippingPolicy, setItemShippingPolicy] = useState(item.itemShippingPolicy ?? '');
    const [useStoreShippingPolicy, setUseStoreShippingPolicy] = useState(item.useStoreShippingPolicy);
    const [useItemShippingPolicy, setUseItemShippingPolicy] = useState(item.useItemShippingPolicy);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [itemShippingPolicyFormErrors, setItemShippingPolicyFormErrors] = useState<ItemShippingPolicyFormErrors>({});
    const [serviceStatus, setServiceStatus] = useState({ type: '', message: '' });

    useEffect(() => {
        if (modalRef.current) {
            bsModalRef.current = new (window as any).bootstrap.Modal(modalRef.current);
            modalRef.current.addEventListener("hidden.bs.modal", onClose);
        }
        return () => {
            modalRef.current?.removeEventListener("hidden.bs.modal", onClose);
        };
    }, []);

    useEffect(() => {
        if (show) {
            setStoreShippingPolicy(seller!.shippingPolicy);
            setItemShippingPolicy(item.itemShippingPolicy ?? '');
            setUseStoreShippingPolicy(item.useStoreShippingPolicy);
            setUseItemShippingPolicy(item.useItemShippingPolicy);
            setServiceStatus({ type: '', message: '' });
            bsModalRef.current?.show();
        } else {
            bsModalRef.current?.hide();
        }
    }, [show]);

    const validateItemShippingPolicy = () => {
        const errors: ItemShippingPolicyFormErrors = {};
        if (useItemShippingPolicy && !itemShippingPolicy.trim()) {
            errors.itemShippingPolicy = "Item shipping policy is required if 'Use Item Shipping Policy' is selected.";
        }
        setItemShippingPolicyFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

const handleItemShippingPolicySubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setServiceStatus({ type: "", message: "" });
    if (validateItemShippingPolicy()) {
            setIsSubmitting(true);
            try {
                const updatedItem = {
                    ...item,
                    useStoreShippingPolicy: useStoreShippingPolicy,
                    useItemShippingPolicy: useItemShippingPolicy,
                    itemShippingPolicy
                };
                const result = await itemService.updateItemShippingPolicy(item.itemId, useStoreShippingPolicy, useItemShippingPolicy, itemShippingPolicy);
                if (result === "Success") {
                    onItemShippingPolicyUpdated(updatedItem);
                    setServiceStatus({ type: "success", message: "Item shipping policy updated successfully." });
                } else {
                    setServiceStatus({ type: "error", message: "Failed to update item shipping policy. Please try again." });
                }
            } catch (error) {
                setServiceStatus({ type: "error", message: "Failed to update item shipping policy." });
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const handleUseStoreShippingPolicyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;
        setUseStoreShippingPolicy(checked);
    };

    const handleUseItemShippingPolicyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;
        setUseItemShippingPolicy(checked);
        if (!checked) {
            
            setItemShippingPolicyFormErrors(prev => ({ ...prev, itemShippingPolicy: undefined }));
        }
    }

    return (
        <div className="modal fade" tabIndex={-1} ref={modalRef} data-bs-backdrop="static" data-bs-keyboard="false" >
            <div className="modal-dialog modal-lg">
                <div className="modal-content rounded-0">
                    <form onSubmit={handleItemShippingPolicySubmit}>
                        <div className="modal-header">
                            <h5 className="modal-title">Edit Shipping Policies</h5>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={() => bsModalRef.current?.hide()}
                                disabled={isSubmitting}
                            ></button>
                        </div>
                        <div className="modal-body">
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <label className="form-label">Use Store Shipping Policy</label>
                                    <div className="form-check form-switch fs-4 ms-2 mb-4">
                                        <input
                                            className="form-check-input fs-3"
                                            type="checkbox"
                                            id="useStoreShippingPolicySwitch"
                                            checked={useStoreShippingPolicy}
                                            onChange={handleUseStoreShippingPolicyChange}
                                        />
                                    </div>
                                    <textarea
                                        className="form-control bg-light"
                                        rows={20}
                                        value={storeShippingPolicy}
                                        readOnly
                                        disabled
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">Use Item Shipping Policy</label>
                                    <div className="form-check form-switch fs-4 ms-2 mb-4">
                                        <input
                                            className="form-check-input fs-3"
                                            type="checkbox"
                                            id="useItemShippingPolicySwitch"
                                            checked={useItemShippingPolicy}
                                            onChange={handleUseItemShippingPolicyChange}
                                        />
                                    </div>
                                    <textarea
                                        className={`form-control ${itemShippingPolicyFormErrors.itemShippingPolicy ? "is-invalid" : ""}`}
                                        rows={20}
                                        value={itemShippingPolicy}
                                        onChange={(e) => setItemShippingPolicy(e.target.value)}
                                        disabled={!useItemShippingPolicy}
                                        placeholder="Enter item-specific shipping policy"
                                    />
                                    {itemShippingPolicyFormErrors.itemShippingPolicy && (
                                        <div className="invalid-feedback">
                                            {itemShippingPolicyFormErrors.itemShippingPolicy}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        {serviceStatus.type.toLowerCase() === "error" && (
                            <div className="col-md-12">
                                <div className="alert alert-danger p-2 mb-0">{serviceStatus.message}</div>
                            </div>
                        )}
                        {serviceStatus.type.toLowerCase() === "success" && (
                            <div className="col-md-12">
                                <div className="alert alert-success p-2 mb-0">{serviceStatus.message}</div>
                            </div>
                        )}

                        <div className="modal-footer d-flex justify-content-between">
                            <button
                                type="button"
                                className="btn btn-outline-dark rounded-0"
                                onClick={onClose}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn btn-dark rounded-0"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Saving ...
                                    </>
                                ) : (
                                    "Save"
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div >
        </div >
    );
};
export default EditItemShippingPolicyComp;